import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getSolicitudes,
  getSolicitudDetalle,
  actualizarEstado,
  actualizarSeguimiento,
  exportarSolicitudes,
  exportarGuiaEnvio,
} from '../api/muestrasApi'
import type {
  FiltrosSolicitudes,
  ActualizarEstadoPayload,
  ActualizarSeguimientoPayload,
} from '../types'

export const solicitudesKeys = {
  all: ['muestras-solicitudes'] as const,
  list: (campaniaId: number, filtros?: FiltrosSolicitudes) =>
    [...solicitudesKeys.all, 'list', campaniaId, filtros] as const,
  detail: (solicitudId: number) =>
    [...solicitudesKeys.all, 'detail', solicitudId] as const,
}

export function useSolicitudes(campaniaId: number, filtros?: FiltrosSolicitudes) {
  return useQuery({
    queryKey: solicitudesKeys.list(campaniaId, filtros),
    queryFn: () => getSolicitudes(campaniaId, filtros),
    enabled: campaniaId > 0,
  })
}

export function useSolicitudDetalle(solicitudId: number) {
  return useQuery({
    queryKey: solicitudesKeys.detail(solicitudId),
    queryFn: () => getSolicitudDetalle(solicitudId),
    enabled: solicitudId > 0,
  })
}

export function useActualizarEstado(_campaniaId?: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ActualizarEstadoPayload) => actualizarEstado(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: solicitudesKeys.all })
      toast.success('Estado actualizado')
    },
    onError: () => toast.error('Error al actualizar estado'),
  })
}

export function useActualizarSeguimiento(_campaniaId?: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ActualizarSeguimientoPayload) => actualizarSeguimiento(payload),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: solicitudesKeys.all })
      queryClient.invalidateQueries({ queryKey: solicitudesKeys.detail(vars.solicitud_id) })
      toast.success('Seguimiento actualizado')
    },
    onError: () => toast.error('Error al actualizar seguimiento'),
  })
}

export function useExportarSolicitudes() {
  return useMutation({
    mutationFn: (campaniaId: number) => exportarSolicitudes(campaniaId),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'solicitudes.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    },
    onError: () => toast.error('Error al exportar'),
  })
}

export function useExportarGuiaEnvio() {
  return useMutation({
    mutationFn: ({ campaniaId, fecha }: { campaniaId: number; fecha?: string }) =>
      exportarGuiaEnvio(campaniaId, fecha),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'guia_envio.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    },
    onError: () => toast.error('Error al exportar guía'),
  })
}
